import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, type CaseListItem, type BranchRef } from '../lib/api'
import type { CaseStatus } from '../../shared/types'
import { SeverityBadge, StatusBadge } from '../components/Badges'
import { formatDate, formatUtcDate } from '../lib/format'

type Tab = CaseStatus | 'all'

const titles: Record<Tab, string> = {
  open: 'Open Damages',
  in_progress: 'In Progress Damages',
  closed: 'Closed Damages',
  all: 'All Damages',
}
const tabPath: Record<Tab, string> = {
  open: '/app/open',
  in_progress: '/app/in-progress',
  closed: '/app/closed',
  all: '/app/cases',
}
const emptyLabel: Record<Tab, string> = {
  open: 'No open cases.',
  in_progress: 'No in progress cases.',
  closed: 'No closed cases.',
  all: 'No cases.',
}

export function CaseListPage({ status }: { status: Tab }) {
  const [params] = useSearchParams()
  // Arriving from a dashboard tile carries that tile's reported-date window, so
  // the number you clicked matches the list you land on.
  const openedFrom = params.get('openedFrom') ?? ''
  const openedTo = params.get('openedTo') ?? ''
  const windowed = Boolean(openedFrom || openedTo)
  const branchParam = params.get('branch') ?? ''

  const [cases, setCases] = useState<CaseListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [sort, setSort] = useState<'asc' | 'desc'>('desc')
  const [branches, setBranches] = useState<BranchRef[]>([])
  const [branch, setBranch] = useState(branchParam)
  const navigate = useNavigate()

  useEffect(() => {
    api
      .listBranches()
      .then((b) => setBranches(b.branches.filter((x) => x.active === 1)))
      .catch(() => setBranches([]))
  }, [])
  // Keep in sync when arriving from a dashboard drill-down link.
  useEffect(() => {
    setBranch(branchParam)
  }, [branchParam])

  useEffect(() => {
    let active = true
    setLoading(true)
    api
      .listCases(status, sort, openedFrom, openedTo, branch || undefined)
      .then((res) => active && setCases(res.cases))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [status, sort, openedFrom, openedTo, branch])

  const showStatus = status === 'all'

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div className="page-title">{titles[status]}</div>
        {!loading && <span style={{ color: 'var(--gray)' }}>{cases.length}</span>}
        {branches.length > 1 && (
          <select
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            aria-label="Filter by branch"
            style={{ marginLeft: 'auto', padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 8, font: 'inherit' }}
          >
            <option value="">All branches</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        )}
      </div>
      <p className="page-sub">Click a case to open it.</p>

      {windowed && (
        <div className="filter-chip">
          <span>
            Reported {formatDate(openedFrom)} – {formatDate(openedTo)}
          </span>
          <Link to={tabPath[status]}>Clear</Link>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 20, color: 'var(--gray)' }}>Loading…</div>
        ) : cases.length === 0 ? (
          <div style={{ padding: 20, color: 'var(--gray)' }}>{emptyLabel[status]}</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Case #</th>
                  <th>Address</th>
                  <th>
                    <button
                      className="link-btn"
                      style={{ color: 'var(--gray)' }}
                      onClick={() => setSort((s) => (s === 'asc' ? 'desc' : 'asc'))}
                    >
                      Incident date {sort === 'asc' ? '↑' : '↓'}
                    </button>
                  </th>
                  {status === 'closed' && <th>Closed</th>}
                  {showStatus && <th>Status</th>}
                  <th>Severity</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((k) => (
                  <tr key={k.id} className="row-link" onClick={() => navigate(`/app/cases/${k.id}`)}>
                    <td style={{ fontWeight: 600 }}>{k.case_number}</td>
                    <td>{k.address ?? '—'}</td>
                    <td>{formatDate(k.incident_date)}</td>
                    {status === 'closed' && <td>{formatUtcDate(k.closed_at)}</td>}
                    {showStatus && (
                      <td>
                        <StatusBadge status={k.status} />
                      </td>
                    )}
                    <td>
                      <SeverityBadge severity={k.severity} />
                    </td>
                    <td style={{ color: 'var(--gray)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={k.description ?? ''}>
                      {k.description ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
